import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon,
  AdjustmentsHorizontalIcon, MagnifyingGlassIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../config/colors';
import AccessOptions from '../access/AccessOptions';

// PDF.js imports
import * as pdfjsLib from 'pdfjs-dist';

// Set worker source to local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const Reader2 = React.memo(({ onClose, onBackToWebsite }) => {
  const navigate = useNavigate();
  const { user, hasPurchased, loading } = useAuth();

  // Enhanced 3D book styling for realistic page effects
  const bookStyles = {
    pageContainer: {
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden',
      willChange: 'transform',
    },
    pageImage: {
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden',
      willChange: 'transform, opacity, filter',
      transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
    },
    pageShadow: {
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
    }
  };


  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfDocument, setPdfDocument] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const [readingProgress, setReadingProgress] = useState(0);
  const [theme, setTheme] = useState('light');
  const [isTurning, setIsTurning] = useState(false);
  const turnDirection = 'next';
  const [retryCount, setRetryCount] = useState(0);
  const [currentPageImage, setCurrentPageImage] = useState('');
  const [nextPageImage, setNextPageImage] = useState('');
  const [flippingPage, setFlippingPage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showPageJumpPanel, setShowPageJumpPanel] = useState(false);
  const [pageJumpInput, setPageJumpInput] = useState('');
  const [showAccessOptions, setShowAccessOptions] = useState(false);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const navigationTimeoutRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const renderRetryCountRef = useRef(0);
  const maxRenderRetries = 3;

  // Mouse-based page flipping states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragCurrentX, setDragCurrentX] = useState(0);

  const [dragDirection, setDragDirection] = useState(null);
  const [pageBend, setPageBend] = useState(0);
  const [pageShadow, setPageShadow] = useState(0);

  // Enhanced page transition states for smooth, realistic animations
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState(null);
  const [pageCurl, setPageCurl] = useState(0);
  const [pageLift, setPageLift] = useState(0);
  const [shadowIntensity, setShadowIntensity] = useState(0);

  // Book appearance states for realistic book effects
  const [coverOpen, setCoverOpen] = useState(false);
  const [showBookCover, setShowBookCover] = useState(true);

  // Access control with production-ready error handling
  useEffect(() => {
    // Wait for loading to complete before checking auth
    if (loading) {
      return;
    }

    if (!user || !hasPurchased) {
      navigate('/');
      return;
    }
  }, [user, hasPurchased, loading, navigate]);

  // Auto-open book cover after a short delay
  useEffect(() => {
    if (!isLoading && !error) {
      const timer = setTimeout(() => {
        setCoverOpen(true);
        setTimeout(() => {
          setShowBookCover(false);
        }, 1000);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error]);

  // Handle book cover interaction
  const handleBookOpen = () => {
    setCoverOpen(true);
    setTimeout(() => {
      setShowBookCover(false);
    }, 1000);
  };

  // Anti-piracy measures
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Block common keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block F12, Ctrl+Shift+I, Ctrl+Shift+C, Ctrl+U, Ctrl+S
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.key === 's')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Screenshot attempt detection (basic)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - could be screenshot attempt
        console.warn('Page visibility changed - potential screenshot attempt');
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load PDF document
  useEffect(() => {
    const loadPdfDocument = async () => {
      try {
        setIsLoading(true);
        setError('');

        const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        // Fetch PDF data through backend
        const response = await fetch(`${BACKEND_URL}/api/pdf-content`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Production: Log removed

          try {
            // Load PDF using PDF.js from our backend proxy (bypasses CORS)
            const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const pdfProxyUrl = `${BACKEND_URL}/api/pdf-proxy`;

            const loadingTask = pdfjsLib.getDocument({
              url: pdfProxyUrl,
              cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
              cMapPacked: true,
            });

            const pdf = await loadingTask.promise;
            // Production: Log removed
            setPdfDocument(pdf);
            setTotalPages(pdf.numPages);

            // Load reading progress
            const savedProgress = localStorage.getItem('fijoli-reading-progress-2.0');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= pdf.numPages) {
                setCurrentPage(progress);
              }
            }
          } catch (pdfError) {
            // Production: Error handled silently
            // Fallback: create basic page structure without PDF.js
            setTotalPages(data.totalPages || 50);
            setPdfDocument({ fallback: true });

            // Load reading progress for fallback mode
            const savedProgress = localStorage.getItem('fijoli-reading-progress-2.0');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= (data.totalPages || 50)) {
                setCurrentPage(progress);
              }
            }
          }
        } else {
          throw new Error(data.error || 'Failed to load PDF');
        }

      } catch (err) {
        // Production: Error handled silently
        setError(`Failed to load book: ${err.message}`);

        // Retry logic for production
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000 * (retryCount + 1));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPdfDocument();
  }, [navigate, retryCount]);

  // Calculate which pages to show (book layout: left page is always even, right page is always odd)
  const leftPageNumber = Math.floor((currentPage - 1) / 2) * 2 + 1;
  const rightPageNumber = leftPageNumber + 1;

  // Render current page and next page for two-page spread
  useEffect(() => {
    let isCancelled = false;
    let currentRenderTask = null;
    let nextRenderTask = null;

    const renderPages = async () => {
      if (!pdfDocument) return;

      // Check if we're in fallback mode (PDF failed to load)
      if (pdfDocument.fallback) {
        // In fallback mode, show placeholder content instead of trying to render PDF pages
        setCurrentPageImage(''); // Clear any existing images
        setNextPageImage('');
        return;
      }

      try {
        // Production: Log removed

        // Render left page (current page)
        const leftCanvas = document.createElement('canvas');
        const leftContext = leftCanvas.getContext('2d');

        if (leftPageNumber <= totalPages) {
          const leftPage = await pdfDocument.getPage(leftPageNumber);
          const leftViewport = leftPage.getViewport({ scale: 1.5 });

          leftCanvas.width = leftViewport.width;
          leftCanvas.height = leftViewport.height;

          const leftRenderContext = {
            canvasContext: leftContext,
            viewport: leftViewport
          };

          currentRenderTask = leftPage.render(leftRenderContext);
          await currentRenderTask.promise;

          if (isCancelled) {
            leftCanvas.remove();
            return;
          }

          // Production: Log removed

          // Convert left page canvas to image
          const leftImageDataUrl = leftCanvas.toDataURL('image/png');
          setCurrentPageImage(leftImageDataUrl);
          // Production: Log removed

          // Clean up left page canvas
          leftCanvas.remove();
        } else {
          // No left page, clear the left side
          setCurrentPageImage('');
          // Production: Log removed
        }

        // Render right page (next page)
        if (rightPageNumber <= totalPages) {
          const rightCanvas = document.createElement('canvas');
          const rightContext = rightCanvas.getContext('2d');

          const rightPage = await pdfDocument.getPage(rightPageNumber);
          const rightViewport = rightPage.getViewport({ scale: 1.5 });

          rightCanvas.width = rightViewport.width;
          rightCanvas.height = rightViewport.height;

          const rightRenderContext = {
            canvasContext: rightContext,
            viewport: rightViewport
          };

          nextRenderTask = rightPage.render(rightRenderContext);
          await nextRenderTask.promise;

          if (isCancelled) {
            rightCanvas.remove();
            return;
          }

          // Production: Log removed

          // Convert right page canvas to image
          const rightImageDataUrl = rightCanvas.toDataURL('image/png');
          setNextPageImage(rightImageDataUrl);
          // Production: Log removed

          // Clean up right page canvas
          rightCanvas.remove();
        } else {
          // No right page, clear the right side
          setNextPageImage('');
          // Production: Log removed
        }

      } catch (err) {
        // Handle specific PDF.js errors
        if (err.name === 'RenderingCancelledException') {
          // Production: Log removed
          return; // Don't show error for cancelled renders
        }

        // Production: Error handled silently

        // Implement retry logic with exponential backoff
        if (renderRetryCountRef.current < maxRenderRetries) {
          renderRetryCountRef.current++;
          const retryDelay = Math.pow(2, renderRetryCountRef.current) * 1000; // Exponential backoff: 2s, 4s, 8s



          setTimeout(() => {
            if (!isCancelled) {
              renderPages();
            }
          }, retryDelay);

          return;
        }

        // Reset retry count and show error after max retries
        renderRetryCountRef.current = 0;
        setError(`Failed to render pages after ${maxRenderRetries} attempts: ${err.message}`);
      }
    };

    if (pdfDocument && currentPage > 0) {
      renderPages();
    }

    // Cleanup function
    return () => {
      isCancelled = true;

      // Cancel render tasks gracefully
      if (currentRenderTask) {
        try {
          currentRenderTask.cancel();
        } catch (cancelError) {

        }
      }

      if (nextRenderTask) {
        try {
          nextRenderTask.cancel();
        } catch (cancelError) {

        }
      }

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (cancelError) {

        }
        renderTaskRef.current = null;
      }
    };
  }, [pdfDocument, currentPage, totalPages, leftPageNumber, rightPageNumber]);

  // Production-ready cleanup for render tasks
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (cancelError) {
          // Silent cleanup in production
        }
        renderTaskRef.current = null;
      }
    };
  }, [currentPage]);

  // Production-ready cleanup for navigation and search timeouts
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }

      // Clear search timeout
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
        window.searchTimeout = null;
      }
    };
  }, []);


  // Save reading progress
  useEffect(() => {
    if (currentPage > 0 && totalPages > 0) {
      const progress = Math.round((currentPage / totalPages) * 100);
      setReadingProgress(progress);
      localStorage.setItem('fijoli-reading-progress-2.0', currentPage.toString());
    }
  }, [currentPage, totalPages]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      navigate('/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);



  // Direct page switching (immediate, no animation)
  const goToPageDirect = useCallback((pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;


    setCurrentPage(pageNumber);
    setIsTurning(false);
    setFlippingPage(null);

    // Reset navigation flag immediately
    isNavigatingRef.current = false;
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
  }, [totalPages]);

  // Search functionality
  const searchInPDF = useCallback(async (query) => {
    if (!pdfDocument || !query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    setIsSearching(true);
    const results = [];

    try {
      // Search through all pages
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();

        // Extract text from text content
        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .toLowerCase();

        const queryLower = query.toLowerCase();
        if (pageText.includes(queryLower)) {
          // Find all occurrences on this page
          const regex = new RegExp(queryLower, 'gi');
          let match;
          let matchCount = 0;

          while ((match = regex.exec(pageText)) !== null && matchCount < 10) {
            results.push({
              page: pageNum,
              index: match.index,
              text: match[0],
              context: pageText.substring(Math.max(0, match.index - 30), match.index + query.length + 30)
            });
            matchCount++;
          }
        }
      }

      setSearchResults(results);
      setCurrentSearchIndex(0);

      if (results.length > 0) {
        // Navigate to first result using direct navigation
        goToPageDirect(results[0].page);
      }
    } catch (error) {
      // Handle search error silently in production
    } finally {
      setIsSearching(false);
    }
  }, [pdfDocument, totalPages, goToPageDirect]);

  // Navigate to next/previous search result
  const goToNextResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    goToPageDirect(searchResults[nextIndex].page);
  }, [searchResults, currentSearchIndex, goToPageDirect]);

  const goToPrevResult = useCallback(() => {
    if (searchResults.length === 0) return;

    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    goToPageDirect(searchResults[prevIndex].page);
  }, [searchResults, currentSearchIndex, goToPageDirect]);



  // Page jump functions
  const handlePageJump = useCallback(() => {
    const pageNumber = parseInt(pageJumpInput);
    if (pageNumber && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setPageJumpInput('');
      setShowPageJumpPanel(false);
    }
  }, [pageJumpInput, totalPages]);

  const handlePageJumpInputChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setPageJumpInput(value);
    }
  }, []);

  const handlePageJumpKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  }, [handlePageJump]);

  // Production-ready search input handler with optimized debouncing
  const handleSearchInputChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear existing timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }

    if (query.trim()) {
      // Optimized debounce search (300ms for better UX)
      window.searchTimeout = setTimeout(() => {
        searchInPDF(query);
      }, 300);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
    }
  }, [searchInPDF]);

  // Simple, smooth page transition function
  const smoothPageTransition = useCallback((direction, targetPage) => {
    if (isTransitioning || isTurning) return;

    setIsTransitioning(true);
    setTransitionDirection(direction);
    setTransitionProgress(0);
    setPageCurl(0);
    setPageLift(0);
    setShadowIntensity(0);

    // Simple, smooth animation duration for desktop
    const startTime = performance.now();
    const duration = 600; // Balanced duration for smooth feel

    const animateTransition = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Simple, smooth easing for natural page turning
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setTransitionProgress(easeProgress);

      // Simple 3D page curl effect
      if (direction === 'next') {
        setPageCurl(easeProgress);
        setPageLift(Math.sin(easeProgress * Math.PI) * 0.1);
        setShadowIntensity(Math.sin(easeProgress * Math.PI) * 0.4);
      } else {
        setPageCurl(easeProgress);
        setPageLift(Math.sin(easeProgress * Math.PI) * 0.08);
        setShadowIntensity(Math.sin(easeProgress * Math.PI) * 0.3);
      }

      if (progress < 1) {
        requestAnimationFrame(animateTransition);
      } else {
        // Transition complete, update page
        setCurrentPage(targetPage);

        // Simple reset
        setTimeout(() => {
          setIsTransitioning(false);
          setTransitionProgress(0);
          setPageCurl(0);
          setPageLift(0);
          setShadowIntensity(0);
          setTransitionDirection(null);
        }, 16);
      }
    };

    requestAnimationFrame(animateTransition);
  }, [isTransitioning, isTurning]);

  // Navigation functions
  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPageNumber = Math.min(currentPage + 2, totalPages);
      smoothPageTransition('next', nextPageNumber);
    }
  }, [currentPage, totalPages, smoothPageTransition]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      const prevPageNumber = Math.max(currentPage - 2, 1);
      smoothPageTransition('prev', prevPageNumber);
    }
  }, [currentPage, smoothPageTransition]);





  // Theme switching
  const switchTheme = useCallback((newTheme) => {
    setTheme(newTheme);
  }, []);

  // Get current theme colors (simple operation, no memoization needed)
  const currentTheme = colors.themes[theme] || colors.themes.light;

  // Anti-piracy: Generate user-specific watermark
  const generateWatermark = useCallback(() => {
    if (!user) {
      console.log('No user available for watermark');
      return 'No User | ' + new Date().toISOString().split('T')[0];
    }
    const userInfo = user.email || user.uid || 'unknown';
    const timestamp = new Date().toISOString().split('T')[0];
    const watermark = `${userInfo} | ${timestamp}`;
    console.log('Generated watermark:', watermark);
    return watermark;
  }, [user]);

  // Enhanced anti-piracy protection with watermark support
  useEffect(() => {
    // Disable right-click context menu
    const disableRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable common keyboard shortcuts for screenshots/copying
    const disableShortcuts = (e) => {
      // Allow navigation keys to pass through
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        return; // Don't block navigation keys
      }

      if (
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // Dev tools
        (e.ctrlKey && e.shiftKey && e.key === 'J') || // Console
        (e.ctrlKey && e.key === 'U') || // View source
        (e.ctrlKey && e.key === 'S') || // Save
        (e.key === 'F12') || // Dev tools
        (e.key === 'PrintScreen') // Screenshot
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Disable text selection on book content
    const disableSelection = (e) => {
      // Check if target is within book-page element
      let element = e.target;
      while (element && element !== document) {
        if (element.classList && element.classList.contains('book-page')) {
          e.preventDefault();
          return false;
        }
        element = element.parentElement;
      }
    };



    // Screenshot attempt detection (basic)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - could be screenshot attempt
        console.warn('Page visibility changed - potential screenshot attempt');
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', disableRightClick);
    document.addEventListener('keydown', disableShortcuts);
    document.addEventListener('selectstart', disableSelection);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('contextmenu', disableRightClick);
      document.removeEventListener('keydown', disableShortcuts);
      document.removeEventListener('selectstart', disableSelection);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT') {
        return; // Don't interfere with input fields
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevPage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextPage();
          break;

        case 'f':
        case 'F':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setShowSearchPanel(!showSearchPanel);
          }
          break;
        case 'Escape':
          setShowSettings(false);
          setShowSearchPanel(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [prevPage, nextPage, showSearchPanel]);

  // Mouse-based page flipping handlers
  const handleMouseDown = useCallback((e) => {
    if (isTurning || isNavigatingRef.current) return;


    e.preventDefault(); // Prevent default to avoid text selection
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragCurrentX(e.clientX);
    setPageBend(0);
    setPageShadow(0);
  }, [isTurning]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || isTurning || isNavigatingRef.current) return;

    const currentX = e.clientX;
    const deltaX = currentX - dragStartX;
    const threshold = 8; // Even lower threshold for ultra-responsive feel

    setDragCurrentX(currentX);

    // Calculate drag progress (0 to 1) with enhanced responsiveness
    const progress = Math.min(Math.abs(deltaX) / 80, 1); // Shorter distance for faster response

    // Determine direction (FIXED: Right drag = go back, Left drag = go forward)
    if (deltaX > threshold) {
      setDragDirection('prev'); // Dragging right = go back
    } else if (deltaX < -threshold) {
      setDragDirection('next'); // Dragging left = go forward
    } else {
      setDragDirection(null);
    }

    // Enhanced page bend effect with realistic physics
    const bend = Math.min(progress * 2.5, 1); // More natural bending curve
    setPageBend(bend);

    // Enhanced shadow effect with depth
    const shadow = Math.min(progress * 1.8, 1); // More realistic shadow progression
    setPageShadow(shadow);


  }, [isDragging, isTurning, dragStartX]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;


    const deltaX = dragCurrentX - dragStartX;
    const threshold = 35; // Ultra-responsive threshold for instant page turns

    setIsDragging(false);
    setPageBend(0);
    setPageShadow(0);

    // Complete page turn if threshold is met
    if (Math.abs(deltaX) > threshold && dragDirection) {
      if (dragDirection === 'next' && currentPage < totalPages) {

        const nextPageNumber = Math.min(currentPage + 2, totalPages);
        smoothPageTransition('next', nextPageNumber);
      } else if (dragDirection === 'prev' && currentPage > 1) {

        const prevPageNumber = Math.max(currentPage - 2, 1);
        smoothPageTransition('prev', prevPageNumber);
      }
    }

    setDragDirection(null);
  }, [isDragging, dragCurrentX, dragStartX, dragDirection, currentPage, totalPages, smoothPageTransition]);

  // Global mouse event listeners for better drag handling
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleMouseMove(e);
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (isDragging) {
        handleMouseUp(e);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch/Swipe Gestures for Mobile
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let isSwiping = false;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isSwiping = true;
    };

    const handleTouchMove = (e) => {
      if (!isSwiping) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = startX - currentX;
      const diffY = startY - currentY;

      // Only handle horizontal swipes
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          // Swipe left - next page
          nextPage();
        } else {
          // Swipe right - previous page
          prevPage();
        }
        isSwiping = false;
      }
    };

    const handleTouchEnd = () => {
      isSwiping = false;
    };

    const bookContainer = document.querySelector('.book-page');
    if (bookContainer) {
      bookContainer.addEventListener('touchstart', handleTouchStart);
      bookContainer.addEventListener('touchmove', handleTouchMove);
      bookContainer.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (bookContainer) {
        bookContainer.removeEventListener('touchstart', handleTouchStart);
        bookContainer.removeEventListener('touchmove', handleTouchMove);
        bookContainer.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [nextPage, prevPage]);

  // Production-ready loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-red-800 text-lg font-semibold">Opening your book...</p>
          <p className="text-red-600 text-sm mt-2">Please wait while we load your reading experience</p>
          {retryCount > 0 && (
            <p className="text-red-600 text-sm mt-2">Retry attempt {retryCount}/3</p>
          )}
        </div>
      </div>
    );
  }

  // Production-ready error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">📖</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Unable to Load Book</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {retryCount < 3 && (
              <button
                onClick={() => {
                  setError('');
                  setIsLoading(true);
                  setRetryCount(prev => prev + 1);
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            )}
            <button
              onClick={() => {
                if (onClose) onClose();
                else if (onBackToWebsite) onBackToWebsite();
                else navigate('/');
              }}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback mode when PDF fails to load but we have basic page structure
  if (pdfDocument && pdfDocument.fallback) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Book Content Unavailable</h2>
          <p className="text-red-700 mb-4">
            We're experiencing technical difficulties loading the book content.
            Your reading progress has been saved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                if (onClose) onClose();
                else if (onBackToWebsite) onBackToWebsite();
                else navigate('/');
              }}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      style={{
        background: currentTheme.background
      }}
    >

      {/* Book Texture Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 2px,
            rgba(139, 69, 19, 0.1) 2px,
            rgba(139, 69, 19, 0.1) 4px
          )`
        }}></div>
      </div>

      {/* Enhanced CSS for page bending effects */}
      <style jsx>{`
        .page-bend-effect {
          transition: transform 0.1s ease-out;
          will-change: transform;
        }
        
        .page-bend-effect:hover {
          transform: scale(1.02);
        }
        
        .page-bend-effect:active {
          transform: scale(0.98);
        }
        
        .drag-cursor {
          cursor: grab;
        }
        
        .drag-cursor:active {
          cursor: grabbing;
        }
        
        .page-shadow {
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
        }
        
        .page-shadow:hover {
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.15));
        }
      `}</style>

      {/* Hidden canvas for PDF rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Top Navigation Bar - Book-themed */}
      <div
        className="absolute top-0 left-0 right-0 z-40 h-16 flex items-center justify-between px-4 shadow-lg backdrop-blur-sm"
        style={{
          backgroundColor: `${currentTheme.surface}CC`,
          borderBottom: `2px solid ${currentTheme.border}`,
          backgroundImage: 'linear-gradient(to right, rgba(139, 69, 19, 0.1), rgba(160, 82, 45, 0.1))'
        }}
      >
        {/* Left side - Back and Title */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (onClose) {
                onClose();
              } else if (onBackToWebsite) {
                onBackToWebsite();
              } else {
                navigate('/');
              }
            }}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: currentTheme.text }}>
              Fijoli Book Reader
            </h1>
            <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
              Pages {currentPage}-{Math.min(currentPage + 1, totalPages)} of {totalPages} ({readingProgress}% complete)
            </p>
          </div>
        </div>

        {/* Center - Page Navigation */}
        <div className="flex items-center space-x-2">
          <motion.button
            onClick={prevPage}
            disabled={currentPage <= 1}
            whileHover={{ scale: currentPage > 1 ? 1.1 : 1 }}
            whileTap={{ scale: currentPage > 1 ? 0.95 : 1 }}
            className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50 border-0 outline-none focus:outline-none"
            style={{
              color: currentPage > 1 ? '#ffffff' : currentTheme.text,
              backgroundColor: currentPage > 1 ? colors.primary.main : 'rgba(107, 114, 128, 0.3)',
              border: 'none',
              outline: 'none'
            }}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </motion.button>

          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium" style={{ color: currentTheme.text }}>
              Pages {currentPage}-{Math.min(currentPage + 1, totalPages)} of {totalPages}
            </span>
            <button
              onClick={() => setShowPageJumpPanel(true)}
              className="px-3 py-1 rounded text-sm transition-colors bg-amber-50 hover:bg-amber-100"
              style={{ color: '#92400e' }}
              title="Go to specific page"
            >
              Go to Page
            </button>
          </div>

          <motion.button
            onClick={nextPage}
            disabled={currentPage >= totalPages}
            whileHover={{ scale: currentPage < totalPages ? 1.1 : 1 }}
            whileTap={{ scale: currentPage < totalPages ? 0.95 : 1 }}
            className="p-2 rounded-lg transition-all duration-200 disabled:opacity-50 border-0 outline-none focus:outline-none"
            style={{
              color: currentPage < totalPages ? '#ffffff' : currentTheme.text,
              backgroundColor: currentPage < totalPages ? colors.primary.main : 'rgba(107, 114, 128, 0.3)',
              border: 'none',
              outline: 'none'
            }}
          >
            <ChevronRightIcon className="h-5 w-5" />
          </motion.button>
        </div>

        {/* Right side - Settings, Search, and Order Hard Copy */}
        <div className="flex items-center space-x-2">
          {/* Order Hard Copy Button - Show for online users who haven't upgraded yet */}
          {user && hasPurchased && !user.hasHardCopy && !user.hasUpgraded && (
            <motion.button
              onClick={() => setShowAccessOptions(true)}
              className="px-4 py-2 rounded-lg transition-all duration-200 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Order Physical Book"
            >
              📖 Hard Copy
            </motion.button>
          )}

          <button
            onClick={() => setShowSearchPanel(!showSearchPanel)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Search"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>

          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Settings"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Book Content */}
      <div className="pt-16 h-full flex items-center justify-center">
        {/* Book Cover - Show when first loading or when cover is open */}
        {showBookCover && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-20"
            initial={{ opacity: 1, scale: 1 }}
            animate={{
              opacity: coverOpen ? 0 : 1,
              scale: coverOpen ? 0.8 : 1,
              rotateY: coverOpen ? -90 : 0
            }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{ perspective: '1000px' }}
          >
            {/* Book Cover Container */}
            <div className="relative w-96 h-[32rem] max-w-[90vw] max-h-[80vh]">
              {/* Front Cover */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-800 via-orange-800 to-red-900 rounded-lg shadow-2xl transform rotate-3 hover:rotate-6 transition-transform duration-300 cursor-pointer"
                onClick={handleBookOpen}>
                {/* Cover Design */}
                <div className="absolute inset-6 bg-gradient-to-br from-amber-700 to-orange-800 rounded flex flex-col items-center justify-center text-center">
                  <div className="text-8xl mb-6">📖</div>
                  <h1 className="text-4xl font-serif font-bold text-white mb-4">FIJOLI</h1>
                  <p className="text-lg text-amber-200 mb-6 font-serif">The Complete Guide</p>
                  <p className="text-sm text-amber-300 font-serif">Click to open</p>
                </div>
              </div>

              {/* Book Spine */}
              <div className="absolute -right-3 top-0 bottom-0 w-4 bg-gradient-to-b from-amber-900 via-orange-800 to-red-900 rounded-r shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-800 to-orange-700 opacity-60"></div>
              </div>

              {/* Book Back Cover */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-900 to-orange-800 rounded-lg transform rotate-3 scale-95 opacity-20"></div>

              {/* 3D Shadow Effects */}
              <div className="absolute -inset-6 bg-gradient-to-br from-black/20 to-transparent rounded-lg blur-xl opacity-60"></div>
            </div>
          </motion.div>
        )}

        {/* Book Pages - Show after cover is opened */}
        <motion.div
          className="relative w-full h-full flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: coverOpen ? 1 : 0,
            scale: coverOpen ? 1 : 0.8
          }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {isLoading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: currentTheme.accent }}></div>
              <p className="text-lg" style={{ color: currentTheme.text }}>Loading your book...</p>
            </div>
          ) : (
            <div
              className="flex w-full h-full relative"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => {
                if (isDragging) {

                  setIsDragging(false);
                  setPageBend(0);
                  setPageShadow(0);
                  setDragDirection(null);
                }
              }}
            >

              {/* Book Container */}
              <div className="relative w-full h-full flex items-center justify-center">
                {/* Book Container */}
                <div className="relative w-full max-w-6xl h-full flex items-center justify-center px-4">
                  {/* Left Page (Current) */}
                  <div
                    className="relative w-1/2 h-full flex items-center justify-center overflow-hidden pr-2"
                    style={bookStyles.pageContainer}
                  >
                    {currentPageImage ? (
                      <div className="relative w-full h-full">
                        {/* Page Container with realistic styling */}
                        <div className="relative w-full h-full max-w-full max-h-full">
                          {/* Page Shadow and 3D Effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-transparent rounded-lg transform rotate-1 scale-95 opacity-30"></div>

                          {/* Main Page Content */}
                          <motion.div
                            className="relative w-full h-full bg-white rounded-lg shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing"
                            style={{
                              ...bookStyles.pageImage,
                              ...bookStyles.pageShadow,
                              filter: theme === 'sepia' ? 'sepia(0.3)' : 'none',
                              transform: `${dragDirection === 'prev' ? `perspective(1500px) rotateY(${pageBend * 30}deg) translateX(${pageBend * 40}px) translateY(${pageBend * 20}px)` : ''}
                              ${dragDirection === 'next' ? `perspective(1500px) rotateY(${-pageBend * 30}deg) translateX(${-pageBend * 40}px) translateY(${pageBend * 20}px)` : ''}
                              ${isTransitioning && transitionDirection === 'prev' ? `perspective(1500px) rotateY(${pageCurl * 30}deg) translateX(${pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}
                              ${isTransitioning && transitionDirection === 'next' ? `perspective(1500px) rotateY(${-pageCurl * 30}deg) translateX(${-pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}`,
                              transformOrigin: dragDirection === 'prev' ? 'right center' : 'left center',
                              boxShadow: dragDirection === 'prev' ?
                                `0 0 ${40 + pageShadow * 60}px rgba(0,0,0,${0.25 + pageShadow * 0.4})` :
                                `0 8px 32px rgba(0,0,0,0.3)`,
                              zIndex: isDragging ? 10 : 1,
                              opacity: isTransitioning ? 1 - transitionProgress * 0.4 : 1,
                            }}
                            initial={{ opacity: 0, x: -20, rotateY: turnDirection === 'prev' ? -15 : 0 }}
                            animate={{
                              opacity: 1,
                              x: 0,
                              rotateY: isTurning && flippingPage === currentPage ?
                                (turnDirection === 'next' ? -15 : 15) : 0
                            }}
                            transition={{
                              duration: 0.4,
                              ease: "easeInOut"
                            }}
                          >
                            {/* Paper Texture Overlay */}
                            <div
                              className="absolute inset-0 pointer-events-none opacity-30 mix-blend-multiply"
                              style={{
                                backgroundImage: `
                                radial-gradient(circle at 20% 20%, rgba(0,0,0,0.02) 1px, transparent 1px),
                                radial-gradient(circle at 40% 40%, rgba(0,0,0,0.015) 0.5px, transparent 0.5px),
                                radial-gradient(circle at 60% 60%, rgba(0,0,0,0.01) 0.8px, transparent 0.8px),
                                radial-gradient(circle at 80% 80%, rgba(0,0,0,0.02) 1.2px, transparent 1.2px),
                                radial-gradient(circle at 30% 70%, rgba(0,0,0,0.01) 0.6px, transparent 0.6px),
                                radial-gradient(circle at 70% 30%, rgba(0,0,0,0.015) 0.9px, transparent 0.9px)
                              `,
                                backgroundSize: '40px 40px, 60px 60px, 80px 80px, 100px 100px, 50px 50px, 70px 70px',
                                backgroundPosition: '0 0, 20px 20px, 40px 40px, 60px 60px, 10px 10px, 30px 30px'
                              }}
                            />
                            {/* Page Image */}
                            <motion.img
                              src={currentPageImage}
                              alt={`Page ${leftPageNumber}`}
                              className="object-contain w-full h-full"
                            />

                            {/* Page Edge Shadow */}
                            <div className="absolute top-0 right-0 bottom-0 w-2 bg-gradient-to-l from-gray-400/40 to-transparent"></div>
                            <div className="absolute top-0 left-0 bottom-0 w-2 bg-gradient-to-r from-gray-400/40 to-transparent"></div>

                            {/* Page Number */}
                            <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg border border-gray-200">
                              <span className="text-sm font-medium text-gray-700">{leftPageNumber}</span>
                            </div>
                          </motion.div>

                          {/* Book Binding Shadow */}
                          <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-gray-800/30 to-transparent rounded-l-lg"></div>
                        </div>

                        {/* Anti-piracy: User-specific watermark - Left page */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-none z-50">
                          <div
                            className="text-sm select-none"
                            style={{
                              color: '#666666',
                              opacity: 0.6,
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {generateWatermark()}
                          </div>
                        </div>

                      </div>
                    ) : (
                      <div className="text-center p-8">
                        <BookOpenIcon className="h-16 w-16 mx-auto mb-4 opacity-50" style={{ color: currentTheme.text }} />
                        <p className="text-lg opacity-70" style={{ color: currentTheme.text }}>
                          Page {leftPageNumber}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Page (Next) */}
                  <div
                    className="relative w-1/2 h-full flex items-center justify-center overflow-hidden pl-2"
                    style={bookStyles.pageContainer}
                  >
                    {nextPageImage ? (
                      <div className="relative w-full h-full">
                        {/* Page Container with realistic styling */}
                        <div className="relative w-full h-full max-w-full max-h-full">
                          {/* Page Shadow and 3D Effect */}
                          <div className="absolute inset-0 bg-gradient-to-l from-gray-800/20 to-transparent rounded-lg transform -rotate-1 scale-95 opacity-30"></div>

                          {/* Main Page Content */}
                          <motion.div
                            className="relative w-full h-full bg-white rounded-lg shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing"
                            style={{
                              ...bookStyles.pageImage,
                              ...bookStyles.pageShadow,
                              filter: theme === 'sepia' ? 'sepia(0.3)' : 'none',
                              transform: `${dragDirection === 'next' ? `perspective(1500px) rotateY(${-pageBend * 30}deg) translateX(${-pageBend * 40}px) translateY(${pageBend * 20}px)` : ''}
                              ${dragDirection === 'prev' ? `perspective(1500px) rotateY(${pageBend * 30}deg) translateX(${pageBend * 40}px) translateY(${pageBend * 20}px)` : ''}
                              ${isTransitioning && transitionDirection === 'next' ? `perspective(1500px) rotateY(${-pageCurl * 30}deg) translateX(${-pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}
                              ${isTransitioning && transitionDirection === 'prev' ? `perspective(1500px) rotateY(${pageCurl * 30}deg) translateX(${pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}`,
                              transformOrigin: dragDirection === 'next' ? 'left center' : 'right center',
                              boxShadow: dragDirection === 'next' ?
                                `0 0 ${40 + pageShadow * 60}px rgba(0,0,0,${0.25 + pageShadow * 0.4})` :
                                `0 8px 32px rgba(0,0,0,0.3)`,
                              zIndex: isDragging ? 10 : 1,
                            }}
                            initial={{ opacity: 0, x: 20, rotateY: turnDirection === 'next' ? 15 : 0 }}
                            animate={{
                              opacity: 1,
                              x: 0,
                              rotateY: isTurning && flippingPage === rightPageNumber ?
                                (turnDirection === 'next' ? 15 : -15) : 0
                            }}
                            transition={{
                              duration: 0.4,
                              ease: "easeInOut"
                            }}
                          >
                            {/* Paper Texture Overlay */}
                            <div
                              className="absolute inset-0 pointer-events-none opacity-30 mix-blend-multiply"
                              style={{
                                backgroundImage: `
                                radial-gradient(circle at 20% 20%, rgba(0,0,0,0.02) 1px, transparent 1px),
                                radial-gradient(circle at 40% 40%, rgba(0,0,0,0.015) 0.5px, transparent 0.5px),
                                radial-gradient(circle at 60% 60%, rgba(0,0,0,0.01) 0.8px, transparent 0.8px),
                                radial-gradient(circle at 80% 80%, rgba(0,0,0,0.02) 1.2px, transparent 1.2px),
                                radial-gradient(circle at 30% 70%, rgba(0,0,0,0.01) 0.6px, transparent 0.6px),
                                radial-gradient(circle at 70% 30%, rgba(0,0,0,0.015) 0.9px, transparent 0.9px)
                              `,
                                backgroundSize: '40px 40px, 60px 60px, 80px 80px, 100px 100px, 50px 50px, 70px 70px',
                                backgroundPosition: '0 0, 20px 20px, 40px 40px, 60px 60px, 10px 10px, 30px 30px'
                              }}
                            />
                            {/* Page Image */}
                            <motion.img
                              src={nextPageImage}
                              alt={`Page ${rightPageNumber}`}
                              className="object-contain w-full h-full"
                            />

                            {/* Page Edge Shadow */}
                            <div className="absolute top-0 right-0 bottom-0 w-2 bg-gradient-to-l from-gray-400/40 to-transparent"></div>
                            <div className="absolute top-0 left-0 bottom-0 w-2 bg-gradient-to-r from-gray-400/40 to-transparent"></div>

                            {/* Page Number */}
                            <div className="absolute bottom-4 left-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg border border-gray-200">
                              <span className="text-sm font-medium text-gray-700">{rightPageNumber}</span>
                            </div>

                            {/* Realistic Bent Corner Effect - Bottom Right */}
                            <div className="absolute bottom-0 right-0 w-20 h-20 pointer-events-none">
                              {/* Main page shadow cast by the curl */}
                              <div
                                className="absolute bottom-0 right-0 w-12 h-12"
                                style={{
                                  background: 'radial-gradient(ellipse at 80% 80%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.08) 30%, rgba(0,0,0,0.03) 60%, transparent 100%)',
                                  clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)'
                                }}
                              />

                              {/* Background shadow (cast on surface beneath) */}
                              <div
                                className="absolute bottom-0 right-0 w-10 h-10"
                                style={{
                                  background: 'radial-gradient(ellipse at 70% 70%, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.05) 70%, transparent 100%)',
                                  clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                                  transform: 'translate(3px, 3px)'
                                }}
                              />

                              {/* Curled page underside (inner surface) */}
                              <div
                                className="absolute bottom-0 right-0 w-8 h-8"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.08) 60%, rgba(0,0,0,0.12) 100%)',
                                  clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                                  transform: 'translate(1px, 1px)'
                                }}
                              />

                              {/* Curled page top surface with highlight */}
                              <div
                                className="absolute bottom-0 right-0 w-6 h-6"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.2) 30%, rgba(255,255,255,0.1) 60%, transparent 100%)',
                                  clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                                  transform: 'translate(0.5px, 0.5px)'
                                }}
                              />

                              {/* Edge highlight for the curl */}
                              <div
                                className="absolute bottom-0 right-0 w-4 h-4"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                                  clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                                  transform: 'translate(-0.5px, -0.5px)'
                                }}
                              />
                            </div>
                          </motion.div>

                          {/* Book Binding Shadow */}
                          <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-gray-800/30 to-transparent rounded-r-lg"></div>
                        </div>

                        {/* Anti-piracy: User-specific watermark - Right page */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 pointer-events-none z-50">
                          <div
                            className="text-sm select-none"
                            style={{
                              color: '#666666',
                              opacity: 0.6,
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {generateWatermark()}
                          </div>
                        </div>

                      </div>
                    ) : (
                      <div className="text-center p-8">
                        <BookOpenIcon className="h-16 w-16 mx-auto mb-4 opacity-50" style={{ color: currentTheme.text }} />
                        <p className="text-lg opacity-70" style={{ color: currentTheme.text }}>
                          Page {rightPageNumber}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Enhanced Realistic Page Turn Overlay */}
              {isTransitioning && (
                <motion.div
                  className="absolute inset-0 pointer-events-none z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Page curl overlay - shows the actual page being turned */}
                  <div
                    className="absolute top-0 bottom-0 w-1/2"
                    style={{
                      left: transitionDirection === 'next' ? '50%' : '0%',
                      background: `linear-gradient(${transitionDirection === 'next' ? '90deg' : '270deg'}, 
                      rgba(0,0,0,0.15) 0%, 
                      rgba(0,0,0,0.08) 40%, 
                      transparent 100%)`,
                      transformOrigin: transitionDirection === 'next' ? 'left center' : 'right center',
                      transform: transitionDirection === 'next'
                        ? `perspective(1500px) rotateY(${-pageCurl * 30}deg) translateX(${-pageCurl * 40}px) translateY(${pageLift * 50}px)`
                        : `perspective(1500px) rotateY(${pageCurl * 30}deg) translateX(${pageCurl * 40}px) translateY(${pageLift * 50}px)`,
                      opacity: Math.min(transitionProgress * 1.5, 0.6),
                      zIndex: 15
                    }}
                  />

                  {/* Dynamic shadow that follows the page curl */}
                  <div
                    className="absolute top-0 bottom-0 w-1/2"
                    style={{
                      left: transitionDirection === 'next' ? '50%' : '0%',
                      background: `radial-gradient(ellipse at ${transitionDirection === 'next' ? 'left' : 'right'} center, 
                      rgba(0,0,0,${0.3 + shadowIntensity * 0.4}) 0%, 
                      rgba(0,0,0,${0.1 + shadowIntensity * 0.2}) 50%, 
                      transparent 100%)`,
                      transformOrigin: transitionDirection === 'next' ? 'left center' : 'right center',
                      transform: transitionDirection === 'next'
                        ? `translateX(${-pageCurl * 30}px) translateZ(${-pageCurl * 15}px)`
                        : `translateX(${pageCurl * 30}px) translateZ(${pageCurl * 15}px)`,
                      opacity: shadowIntensity * 0.8,
                      zIndex: 14
                    }}
                  />
                </motion.div>
              )}

              {/* Legacy Page Turn Overlay for backward compatibility */}
              {isTurning && (
                <motion.div
                  className="absolute inset-0 pointer-events-none z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Page flip shadow effect */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(${turnDirection === 'next' ? '90deg' : '270deg'}, 
                      rgba(0,0,0,0.1) 0%, 
                      rgba(0,0,0,0.05) 50%, 
                      transparent 100%)`
                    }}
                  />

                  {/* Page curl effect */}
                  <motion.div
                    className="absolute top-0 bottom-0 w-1/2"
                    style={{
                      left: turnDirection === 'next' ? '50%' : '0%',
                      background: `linear-gradient(${turnDirection === 'next' ? '90deg' : '270deg'}, 
                      rgba(0,0,0,0.2) 0%, 
                      rgba(0,0,0,0.1) 30%, 
                      transparent 100%)`,
                      transformOrigin: turnDirection === 'next' ? 'left center' : 'right center'
                    }}
                    animate={{
                      scaleX: [0, 1, 0],
                      opacity: [0, 0.3, 0]
                    }}
                    transition={{
                      duration: 0.8,
                      ease: "easeInOut"
                    }}
                  />
                </motion.div>
              )}






            </div>
          )}

          {/* Close the book pages motion.div */}
        </motion.div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-64 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Settings</h3>

              {/* Theme Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Theme</label>
                <div className="flex space-x-2">
                  {['light', 'dark', 'sepia'].map((themeOption) => (
                    <button
                      key={themeOption}
                      onClick={() => switchTheme(themeOption)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${theme === themeOption ? 'ring-2' : ''
                        }`}
                      style={{
                        backgroundColor: theme === themeOption ? colors.primary.main : currentTheme.input.background,
                        color: theme === themeOption ? 'white' : currentTheme.text,
                        borderColor: currentTheme.border
                      }}
                    >
                      {themeOption.charAt(0).toUpperCase() + themeOption.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Panel */}
      <AnimatePresence>
        {showSearchPanel && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSearchPanel(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Search</h3>

              {/* Search Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && searchInPDF(searchQuery)}
                    placeholder="Search in book..."
                    className="flex-1 px-3 py-2 rounded-l border text-sm"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border
                    }}
                  />
                  <button
                    onClick={() => searchInPDF(searchQuery)}
                    disabled={!searchQuery.trim() || isSearching}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      '🔍'
                    )}
                  </button>
                </div>
                {isSearching && (
                  <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: currentTheme.accent }}></div>
                  </div>
                )}
              </div>

              {/* Search Navigation */}
              {searchResults.length > 0 && (
                <div className="flex items-center space-x-1 mb-4">
                  <button
                    onClick={goToPrevResult}
                    disabled={searchResults.length === 0}
                    className="p-2 rounded hover:bg-opacity-20 disabled:opacity-50 transition-colors"
                    style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
                    title="Previous Result"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <span className="text-sm px-2" style={{ color: currentTheme.text }}>
                    {currentSearchIndex + 1} of {searchResults.length}
                  </span>

                  <button
                    onClick={goToNextResult}
                    disabled={searchResults.length === 0}
                    className="p-2 rounded hover:bg-opacity-20 disabled:opacity-50 transition-colors"
                    style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
                    title="Next Result"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                    Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}:
                  </h4>
                  <div className="space-y-2">
                    {searchResults.map((result, index) => (
                      <div
                        key={`${result.page}-${index}`}
                        className={`p-3 rounded border cursor-pointer transition-colors ${index === currentSearchIndex ? 'ring-2' : ''
                          }`}
                        style={{
                          backgroundColor: index === currentSearchIndex ? `${colors.primary.main}20` : currentTheme.input.background,
                          borderColor: index === currentSearchIndex ? colors.primary.main : currentTheme.input.border,
                          color: currentTheme.text
                        }}
                        onClick={() => {
                          setCurrentSearchIndex(index);
                          goToPageDirect(result.page);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: currentTheme.accent }}>
                            Page {result.page}
                          </span>
                          <span className="text-xs opacity-70">
                            Result {index + 1}
                          </span>
                        </div>
                        <p className="text-sm opacity-80">
                          ...{result.context}...
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchQuery && !isSearching && searchResults.length === 0 && (
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
                    No results found for "{searchQuery}"
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Page Jump Panel */}
      <AnimatePresence>
        {showPageJumpPanel && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPageJumpPanel(false)}
            />
            <motion.div
              className="absolute top-16 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-50"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Go to Page</h3>

              {/* Page Jump Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={pageJumpInput}
                    onChange={handlePageJumpInputChange}
                    onKeyPress={handlePageJumpKeyPress}
                    placeholder={`1-${totalPages}`}
                    className="flex-1 px-3 py-2 rounded-l border text-sm"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border
                    }}
                  />
                  <button
                    onClick={handlePageJump}
                    disabled={!pageJumpInput.trim()}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    Go
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter a page number between 1 and {totalPages}
                </p>
              </div>


              {/* Close Button */}
              <button
                onClick={() => setShowPageJumpPanel(false)}
                className="w-full px-4 py-2 rounded text-sm transition-colors"
                style={{
                  backgroundColor: currentTheme.input.background,
                  color: currentTheme.text,
                  borderColor: currentTheme.border
                }}
              >
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Access Options Modal */}
      <AccessOptions
        isOpen={showAccessOptions}
        onClose={() => setShowAccessOptions(false)}
        onAccessGranted={() => {
          // User already has access, just close the modal
          setShowAccessOptions(false);
        }}
      />

      {/* Anti-piracy: Copyright Notice */}
      <div className="fixed top-2 left-1/2 transform -translate-x-1/2 z-30 hidden md:block">
        <div className="text-center">
          <p className="text-xs opacity-60 text-gray-600 bg-white px-3 py-1 rounded-full shadow-sm">
            © Fijoli - All rights reserved. Unauthorized copying prohibited.
          </p>
        </div>
      </div>
    </div>
  );
});

export default Reader2;